import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";

import {
  FiArrowLeft,
  FiDownload,
  FiMessageCircle,
  FiPrinter,
  FiRefreshCw,
} from "react-icons/fi";

import {
  useDispatch,
  useSelector,
} from "react-redux";

import {
  useHistory,
  useLocation,
  useParams,
} from "react-router-dom";

import {
  fetchQuotationById,
  quotationLoadingSelector,
  selectedQuotationSelector,
} from "../../../../../../store/billing";

import "./QuotationPrintPage.css";
import useAuth from "../../../../../../hooks/useAuth";


/*
|--------------------------------------------------------------------------
| GENERIC HELPERS
|--------------------------------------------------------------------------
*/

const toNumber = (
  value,
  fallback = 0
) => {
  const number =
    Number(value);

  return Number.isFinite(
    number
  )
    ? number
    : fallback;
};

const roundMoney = (
  value
) => {
  return (
    Math.round(
      (
        toNumber(value) +
        Number.EPSILON
      ) *
        100
    ) / 100
  );
};

const getNestedValue = (
  object,
  path
) => {
  if (
    !object ||
    !path
  ) {
    return undefined;
  }

  return String(path)
    .split(".")
    .reduce(
      (
        result,
        key
      ) =>
        result?.[key],
      object
    );
};

const getFirstValue = (
  object,
  paths = [],
  fallback = ""
) => {
  for (
    const path of paths
  ) {
    const value =
      getNestedValue(
        object,
        path
      );

    if (
      value !== undefined &&
      value !== null &&
      value !== ""
    ) {
      return value;
    }
  }

  return fallback;
};

const formatCurrency = (
  value
) => {
  return new Intl.NumberFormat(
    "en-IN",
    {
      style: "currency",
      currency: "INR",
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }
  ).format(
    toNumber(value)
  );
};

const formatDate = (
  value
) => {
  if (!value) {
    return "-";
  }

  const date =
    new Date(value);

  if (
    Number.isNaN(
      date.getTime()
    )
  ) {
    return "-";
  }

  return new Intl.DateTimeFormat(
    "en-IN",
    {
      day: "2-digit",
      month: "short",
      year: "numeric",
    }
  ).format(date);
};

const formatLabel = (
  value
) => {
  if (!value) {
    return "-";
  }

  return String(value)
    .replace(
      /_/g,
      " "
    )
    .toLowerCase()
    .replace(
      /\b\w/g,
      (character) =>
        character.toUpperCase()
    );
};

const getAddressText = (
  address
) => {
  if (!address) {
    return "-";
  }

  if (
    typeof address ===
    "string"
  ) {
    return (
      address.trim() ||
      "-"
    );
  }

  return [
    address.addressLine1 ||
      address.line1,
    address.addressLine2 ||
      address.line2,
    address.address,
    address.city,
    address.state,
    address.pincode ||
      address.postalCode,
    address.country,
  ]
    .filter(Boolean)
    .join(", ") || "-";
};

const getInitial = (
  value
) => {
  return (
    String(
      value || "C"
    )
      .trim()
      .charAt(0)
      .toUpperCase() ||
    "C"
  );
};


/*
|--------------------------------------------------------------------------
| SHARED INVOICE BRANDING / SIGNATURE LOGIC
|--------------------------------------------------------------------------
|
| This mirrors the working Sale Invoice InvoiceBrandAssets behavior:
| UPLOAD / DRAW => image
| TEXT          => styled text
| NONE          => label only
|
|--------------------------------------------------------------------------
*/

const resolveInvoiceSignature =
  (
    business
  ) => {

    const branding =
      business
        ?.invoiceBranding ||
      {};

    const rawMode =
      String(
        branding
          ?.signatureMode ||
        business
          ?.signatureMode ||
        ""
      )
        .trim()
        .toUpperCase();

    const image =
      String(
        branding
          ?.signatureImage ||

        business
          ?.signature ||

        business
          ?.authorizedSignature ||

        ""
      ).trim();

    const signatureText =
      String(
        branding
          ?.signatureText ||

        business
          ?.signatureText ||

        ""
      ).trim();

    const style =
      String(
        branding
          ?.signatureStyle ||

        business
          ?.signatureStyle ||

        "STYLE_1"
      )
        .trim()
        .toUpperCase();

    const mode =
      rawMode ||
      (
        image
          ?
          "UPLOAD"
          :
          (
            signatureText
              ?
              "TEXT"
              :
              "NONE"
          )
      );

    return {
      mode,
      image,
      text:
        signatureText,
      style,
    };

  };


const InvoiceSignature =
  ({
    businessSnapshot,
    showLabel = true,
    className = "",
  }) => {

    const {
      mode,
      image,
      text:
        signatureText,
      style,
    } =
      resolveInvoiceSignature(
        businessSnapshot
      );

    const hasImage =
      (
        mode ===
        "UPLOAD" ||
        mode ===
        "DRAW"
      ) &&
      Boolean(
        image
      );

    const hasText =
      mode ===
      "TEXT" &&
      Boolean(
        signatureText
      );

    if (
      !hasImage &&
      !hasText
    ) {

      return (
        showLabel
          ?
          (
            <div
              className={[
                "invoice-template-signature",
                "invoice-template-signature--empty",
                className,
              ]
                .filter(
                  Boolean
                )
                .join(
                  " "
                )}
            >
              <strong>
                Authorized Signatory
              </strong>
            </div>
          )
          :
          null
      );

    }

    return (

      <div
        className={[
          "invoice-template-signature",
          className,
        ]
          .filter(
            Boolean
          )
          .join(
            " "
          )}
        style={{
          minWidth: 190,
          textAlign: "center",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "flex-end",
          gap: 6,
        }}
      >

        {
          hasImage &&
          (
            <img
              src={
                image
              }
              alt="Authorized Signature"
              style={{
                width: "auto",
                maxWidth: 170,
                height: "auto",
                maxHeight: 85,
                objectFit: "contain",
                display: "block",
                margin: "0 auto",
              }}
            />
          )
        }

        {
          hasText &&
          (
            <div
              className={[
                "invoice-template-signature__text",
                `invoice-template-signature__text--${style.toLowerCase()}`,
              ].join(
                " "
              )}
            >
              {signatureText}
            </div>
          )
        }

        {
          showLabel &&
          (
            <strong>
              Authorized Signatory
            </strong>
          )
        }

      </div>

    );

  };

/*
|--------------------------------------------------------------------------
| ITEM NORMALIZATION
|--------------------------------------------------------------------------
*/

const normalizePrintItem = (
  rawItem,
  index
) => {
  const quantity =
    toNumber(
      getFirstValue(
        rawItem,
        [
          "quantity",
          "qty",
        ],
        0
      )
    );

  const rate =
    toNumber(
      getFirstValue(
        rawItem,
        [
          "rate",
          "unitPrice",
          "price",
          "sellingPrice",
          "productSnapshot.sellingPrice",
          "productSnapshot.price",
        ],
        0
      )
    );

  const grossAmount =
    roundMoney(
      toNumber(
        getFirstValue(
          rawItem,
          [
            "grossAmount",
            "subtotal",
            "subTotal",
            "baseAmount",
          ],
          quantity *
            rate
        )
      )
    );

  const discountType =
    getFirstValue(
      rawItem,
      [
        "discountType",
        "discount.type",
      ],
      "NONE"
    );

  const discountValue =
    toNumber(
      getFirstValue(
        rawItem,
        [
          "discountValue",
          "discount.value",
          "discountPercentage",
          "discountRate",
        ],
        0
      )
    );

  let discountAmount =
    toNumber(
      getFirstValue(
        rawItem,
        [
          "discountAmount",
          "lineDiscountAmount",
          "calculatedDiscount",
          "discount.amount",
          "discount.discountAmount",
        ],
        0
      )
    );

  if (
    discountAmount === 0 &&
    discountValue > 0
  ) {
    discountAmount =
      String(
        discountType
      ).toUpperCase() ===
      "PERCENTAGE"
        ? roundMoney(
            grossAmount *
              discountValue /
              100
          )
        : Math.min(
            roundMoney(
              discountValue
            ),
            grossAmount
          );
  }

  const priceMode =
    String(
      getFirstValue(
        rawItem,
        [
          "priceMode",
        ],
        "TAX_EXCLUSIVE"
      )
    ).toUpperCase();

  const taxPercentage =
    toNumber(
      getFirstValue(
        rawItem,
        [
          "taxPercentage",
          "taxRate",
          "gstRate",
          "tax.percentage",
        ],
        0
      )
    );

  const cessPercentage =
    toNumber(
      getFirstValue(
        rawItem,
        [
          "cessPercentage",
          "cessRate",
        ],
        0
      )
    );

  const discountedAmount =
    roundMoney(
      Math.max(
        grossAmount -
          discountAmount,
        0
      )
    );

  let taxableAmount =
    toNumber(
      getFirstValue(
        rawItem,
        [
          "taxableAmount",
          "netTaxableAmount",
        ],
        NaN
      ),
      NaN
    );

  let taxAmount =
    toNumber(
      getFirstValue(
        rawItem,
        [
          "taxAmount",
          "calculatedTax",
          "tax.amount",
          "gstAmount",
        ],
        NaN
      ),
      NaN
    );

  if (
    !Number.isFinite(
      taxableAmount
    ) ||
    !Number.isFinite(
      taxAmount
    )
  ) {
    if (
      priceMode ===
      "TAX_INCLUSIVE"
    ) {
      const combinedRate =
        taxPercentage +
        cessPercentage;

      taxableAmount =
        combinedRate > 0
          ? roundMoney(
              discountedAmount /
                (
                  1 +
                  combinedRate /
                    100
                )
            )
          : discountedAmount;

      taxAmount =
        roundMoney(
          discountedAmount -
            taxableAmount
        );
    } else {
      taxableAmount =
        discountedAmount;

      taxAmount =
        roundMoney(
          taxableAmount *
            taxPercentage /
            100
        );
    }
  }

  const cessAmount =
    toNumber(
      getFirstValue(
        rawItem,
        [
          "cessAmount",
        ],
        priceMode ===
          "TAX_EXCLUSIVE"
          ? roundMoney(
              taxableAmount *
                cessPercentage /
                100
            )
          : 0
      )
    );

  const calculatedTotal =
    priceMode ===
    "TAX_INCLUSIVE"
      ? discountedAmount
      : roundMoney(
          taxableAmount +
            taxAmount +
            cessAmount
        );

  const totalAmount =
    roundMoney(
      toNumber(
        getFirstValue(
          rawItem,
          [
            "totalAmount",
            "lineTotal",
            "grandTotal",
            "netAmount",
            "amount",
          ],
          calculatedTotal
        )
      )
    );

  const itemName =
    getFirstValue(
      rawItem,
      [
        "itemName",
        "productName",
        "name",
        "title",
        "productSnapshot.name",
        "productSnapshot.productName",
        "product.name",
        "product.productName",
      ],
      ""
    ) ||
    getFirstValue(
      rawItem,
      [
        "description",
      ],
      "Manual Item"
    );

  return {
    id:
      rawItem?._id ||
      rawItem?.id ||
      index,

    itemName,

    description:
      getFirstValue(
        rawItem,
        [
          "description",
          "itemDescription",
        ],
        ""
      ),

    hsnSacCode:
      getFirstValue(
        rawItem,
        [
          "hsnSacCode",
          "hsnCode",
          "sacCode",
          "productSnapshot.hsnSacCode",
          "productSnapshot.hsnCode",
          "product.hsnSacCode",
          "product.hsnCode",
        ],
        "-"
      ),

    quantity,

    unit:
      getFirstValue(
        rawItem,
        [
          "unit",
          "uom",
          "unitName",
        ],
        ""
      ),

    rate,
    grossAmount,
    discountType,
    discountValue,
    discountAmount,
    taxableAmount,
    taxPercentage,
    taxAmount,
    cessAmount,
    totalAmount,

    isManual:
      !rawItem?.productId ||
      String(
        rawItem?.itemType ||
        ""
      ).toUpperCase() ===
        "MANUAL",
  };
};

/*
|--------------------------------------------------------------------------
| COMPONENT
|--------------------------------------------------------------------------
*/

const QuotationPrintPage =
  () => {
    const { user } =
      useAuth();

    const tenant =
      user?.tenant || {};

    const printRef =
      useRef(null);

    const dispatch =
      useDispatch();

    const history =
      useHistory();

    const location =
      useLocation();

    const { id } =
      useParams();

    const quotation =
      useSelector(
        selectedQuotationSelector
      );

    const loadingState =
      useSelector(
        quotationLoadingSelector
      ) || {};

    const [
      actionMessage,
      setActionMessage,
    ] = useState("");

    const loading =
      Boolean(
        loadingState.detailsLoading ||
        loadingState.fetchByIdLoading ||
        loadingState.loading
      );

    /*
    |--------------------------------------------------------------------------
    | LOAD QUOTATION
    |--------------------------------------------------------------------------
    */

    useEffect(() => {
      if (id) {
        dispatch(
          fetchQuotationById(
            id
          )
        );
      }
    }, [
      dispatch,
      id,
    ]);

    /*
    |--------------------------------------------------------------------------
    | NORMALIZED DATA
    |--------------------------------------------------------------------------
    */

    const rawItems =
      useMemo(() => {
        const source =
          getFirstValue(
            quotation,
            [
              "items",
              "document.items",
              "quotation.items",
            ],
            []
          );

        return Array.isArray(
          source
        )
          ? source
          : [];
      }, [
        quotation,
      ]);

    const items =
      useMemo(
        () =>
          rawItems.map(
            (
              item,
              index
            ) =>
              normalizePrintItem(
                item,
                index
              )
          ),
        [
          rawItems,
        ]
      );

    const totals =
      useMemo(() => {
        const calculated =
          items.reduce(
            (
              result,
              item
            ) => ({
              subtotal:
                result.subtotal +
                item.grossAmount,

              totalDiscount:
                result.totalDiscount +
                item.discountAmount,

              taxableAmount:
                result.taxableAmount +
                item.taxableAmount,

              totalTax:
                result.totalTax +
                item.taxAmount,

              cessAmount:
                result.cessAmount +
                item.cessAmount,

              grandTotal:
                result.grandTotal +
                item.totalAmount,
            }),
            {
              subtotal: 0,
              totalDiscount: 0,
              taxableAmount: 0,
              totalTax: 0,
              cessAmount: 0,
              grandTotal: 0,
            }
          );

        const savedSubtotal =
          toNumber(
            getFirstValue(
              quotation,
              [
                "totals.subtotal",
                "totals.subTotal",
                "totals.grossAmount",
                "subtotal",
                "subTotal",
                "grossAmount",
              ],
              0
            )
          );

        const subtotal =
          savedSubtotal > 0
            ? savedSubtotal
            : calculated.subtotal;

        const savedDiscount =
          toNumber(
            getFirstValue(
              quotation,
              [
                "totals.discountAmount",
                "totals.totalDiscount",
                "discountAmount",
                "totalDiscount",
              ],
              0
            )
          );

        const totalDiscount =
          savedDiscount > 0
            ? savedDiscount
            : calculated.totalDiscount;

        const savedTaxable =
          toNumber(
            getFirstValue(
              quotation,
              [
                "totals.taxableAmount",
                "taxableAmount",
              ],
              0
            )
          );

        const taxableAmount =
          savedTaxable > 0
            ? savedTaxable
            : calculated.taxableAmount;

        const cgstAmount =
          toNumber(
            getFirstValue(
              quotation,
              [
                "totals.cgstAmount",
                "cgstAmount",
              ],
              0
            )
          );

        const sgstAmount =
          toNumber(
            getFirstValue(
              quotation,
              [
                "totals.sgstAmount",
                "sgstAmount",
              ],
              0
            )
          );

        const igstAmount =
          toNumber(
            getFirstValue(
              quotation,
              [
                "totals.igstAmount",
                "igstAmount",
              ],
              0
            )
          );

        const savedTotalTax =
          toNumber(
            getFirstValue(
              quotation,
              [
                "totals.taxAmount",
                "totals.totalTax",
                "taxAmount",
                "totalTax",
              ],
              0
            )
          );

        const totalTax =
          savedTotalTax > 0
            ? savedTotalTax
            : calculated.totalTax;

        const roundOffAmount =
          toNumber(
            getFirstValue(
              quotation,
              [
                "totals.roundOffAmount",
                "totals.roundOff",
                "roundOffAmount",
                "roundOff",
              ],
              0
            )
          );

        const savedGrandTotal =
          toNumber(
            getFirstValue(
              quotation,
              [
                "totals.grandTotal",
                "totals.totalAmount",
                "grandTotal",
                "totalAmount",
                "netAmount",
              ],
              0
            )
          );

        const grandTotal =
          savedGrandTotal > 0
            ? savedGrandTotal
            : roundMoney(
                calculated.grandTotal +
                  roundOffAmount
              );

        const isInterState =
          igstAmount > 0 ||
          String(
            getFirstValue(
              quotation,
              [
                "taxType",
                "supplyType",
              ],
              ""
            )
          ).toUpperCase() ===
            "INTER_STATE";

        let finalCgst =
          cgstAmount;

        let finalSgst =
          sgstAmount;

        let finalIgst =
          igstAmount;

        if (
          totalTax > 0 &&
          cgstAmount === 0 &&
          sgstAmount === 0 &&
          igstAmount === 0
        ) {
          if (
            isInterState
          ) {
            finalIgst =
              totalTax;
          } else {
            finalCgst =
              roundMoney(
                totalTax / 2
              );

            finalSgst =
              roundMoney(
                totalTax -
                  finalCgst
              );
          }
        }

        return {
          subtotal:
            roundMoney(
              subtotal
            ),

          totalDiscount:
            roundMoney(
              totalDiscount
            ),

          taxableAmount:
            roundMoney(
              taxableAmount
            ),

          cgstAmount:
            roundMoney(
              finalCgst
            ),

          sgstAmount:
            roundMoney(
              finalSgst
            ),

          igstAmount:
            roundMoney(
              finalIgst
            ),

          totalTax:
            roundMoney(
              totalTax
            ),

          roundOffAmount:
            roundMoney(
              roundOffAmount
            ),

          grandTotal:
            roundMoney(
              grandTotal
            ),

          isInterState:
            finalIgst > 0,
        };
      }, [
        quotation,
        items,
      ]);

    const company =
      useMemo(() => {
        const tenantAddress =
          tenant?.address || {};

        const tenantBranding =
          tenant?.branding || {};

        const tenantSettings =
          tenant?.settings || {};

        const documentCompany =
          getFirstValue(
            quotation,
            [
              "companySnapshot",
              "businessSnapshot",
              "firmSnapshot",
              "company",
              "firm",
            ],
            {}
          ) || {};

        return {
          name:
            String(
              tenant?.legalName ||
              tenant?.name ||
              getFirstValue(
                documentCompany,
                [
                  "businessName",
                  "companyName",
                  "firmName",
                  "legalName",
                  "name",
                ],
                "Business"
              )
            )
              .trim()
              .toUpperCase(),

          legalName:
            String(
              tenant?.legalName ||
              tenant?.name ||
              ""
            )
              .trim()
              .toUpperCase(),

          code:
            tenant?.code ||
            "",

          address:
            getAddressText({
              line1:
                tenantAddress?.line1 ||
                "",
              line2:
                tenantAddress?.line2 ||
                "",
              city:
                tenantAddress?.city ||
                "",
              state:
                tenantAddress?.state ||
                tenantSettings?.state ||
                "",
              postalCode:
                tenantAddress?.postalCode ||
                "",
              country:
                tenantAddress?.country ||
                tenantSettings?.country ||
                "",
            }) !== "-"
              ? getAddressText({
                  line1:
                    tenantAddress?.line1 ||
                    "",
                  line2:
                    tenantAddress?.line2 ||
                    "",
                  city:
                    tenantAddress?.city ||
                    "",
                  state:
                    tenantAddress?.state ||
                    tenantSettings?.state ||
                    "",
                  postalCode:
                    tenantAddress?.postalCode ||
                    "",
                  country:
                    tenantAddress?.country ||
                    tenantSettings?.country ||
                    "",
                })
              : getAddressText(
                  getFirstValue(
                    documentCompany,
                    [
                      "address",
                      "registeredAddress",
                      "billingAddress",
                    ],
                    ""
                  )
                ),

          mobile:
            tenant?.mobile ||
            getFirstValue(
              documentCompany,
              [
                "mobile",
                "phone",
                "contactNumber",
              ],
              ""
            ),

          email:
            tenant?.email ||
            getFirstValue(
              documentCompany,
              [
                "email",
              ],
              ""
            ),

          gstin:
            tenant?.gstNumber ||
            getFirstValue(
              documentCompany,
              [
                "gstin",
                "gstNumber",
              ],
              ""
            ),

          pan:
            tenant?.panNumber ||
            getFirstValue(
              documentCompany,
              [
                "pan",
                "panNumber",
              ],
              ""
            ),

          logo:
            tenant?.invoiceBranding?.logo ||
            tenantBranding?.logo ||
            getFirstValue(
              documentCompany,
              [
                "logo",
                "logoUrl",
                "businessLogo",
              ],
              ""
            ),

          currency:
            tenantSettings?.currency ||
            "INR",

          timezone:
            tenantSettings?.timezone ||
            "Asia/Kolkata",

          dateFormat:
            tenantSettings?.dateFormat ||
            "DD-MM-YYYY",
        };
      }, [
        quotation,
        tenant,
      ]);

    const quotationBusinessSnapshot =
      useMemo(
        () => ({
          ...company,

          businessName:
            company.name,

          name:
            company.name,

          address:
            company.address,

          gstin:
            company.gstin,

          mobile:
            company.mobile,

          email:
            company.email,

          branding:
            tenant
              ?.branding ||
            {},

          invoiceBranding:
            tenant
              ?.invoiceBranding ||
            {},

          logo:
            tenant
              ?.invoiceBranding
              ?.logo ||
            company.logo ||
            tenant
              ?.branding
              ?.logo ||
            "",

          signature:
            tenant
              ?.invoiceBranding
              ?.signatureImage ||
            "",

          signatureMode:
            tenant
              ?.invoiceBranding
              ?.signatureMode ||
            "NONE",

          signatureText:
            tenant
              ?.invoiceBranding
              ?.signatureText ||
            "",

          signatureStyle:
            tenant
              ?.invoiceBranding
              ?.signatureStyle ||
            "STYLE_1",
        }),
        [
          company,
          tenant,
        ]
      );


    const customer =
      useMemo(() => {
        const savedCustomer =
          getFirstValue(
            quotation,
            [
              "customer",
            ],
            {}
          ) || {};

        const snapshot =
          getFirstValue(
            quotation,
            [
              "customerSnapshot",
            ],
            {}
          ) || {};

        const billingAddress =
          getFirstValue(
            quotation,
            [
              "billingAddressSnapshot",
              "billingAddress",
            ],
            {}
          );

        const shippingAddress =
          getFirstValue(
            quotation,
            [
              "shippingAddressSnapshot",
              "shippingAddress",
            ],
            billingAddress
          );

        return {
          name:
            getFirstValue(
              snapshot,
              [
                "customerName",
                "name",
                "companyName",
              ],
              getFirstValue(
                savedCustomer,
                [
                  "customerName",
                  "name",
                  "companyName",
                ],
                "Walk-in Customer"
              )
            ),

          mobile:
            getFirstValue(
              snapshot,
              [
                "mobile",
                "phone",
              ],
              getFirstValue(
                savedCustomer,
                [
                  "mobile",
                  "phone",
                ],
                ""
              )
            ),

          email:
            getFirstValue(
              snapshot,
              [
                "email",
              ],
              getFirstValue(
                savedCustomer,
                [
                  "email",
                ],
                ""
              )
            ),

          gstin:
            getFirstValue(
              snapshot,
              [
                "gstin",
                "gstNumber",
              ],
              getFirstValue(
                savedCustomer,
                [
                  "gstin",
                  "gstNumber",
                ],
                ""
              )
            ),

          billingAddress:
            getAddressText(
              billingAddress
            ),

          shippingAddress:
            getAddressText(
              shippingAddress
            ),
        };
      }, [
        quotation,
      ]);

    const documentNumber =
      getFirstValue(
        quotation,
        [
          "documentNumber",
          "quotationNumber",
          "quotationNo",
          "number",
        ],
        "-"
      );

    const status =
      getFirstValue(
        quotation,
        [
          "status",
          "documentStatus",
        ],
        "DRAFT"
      );

    const documentDate =
      getFirstValue(
        quotation,
        [
          "documentDate",
          "quotationDate",
          "createdAt",
        ],
        ""
      );

    const validUntil =
      getFirstValue(
        quotation,
        [
          "validUntil",
          "expiryDate",
        ],
        ""
      );

    const salesChannel =
      getFirstValue(
        quotation,
        [
          "salesChannel",
        ],
        "-"
      );

    const priceMode =
      getFirstValue(
        quotation,
        [
          "priceMode",
        ],
        "-"
      );

    const notes =
      getFirstValue(
        quotation,
        [
          "customerNotes",
          "notes",
        ],
        ""
      );

    const terms =
      getFirstValue(
        quotation,
        [
          "termsAndConditions",
          "terms",
        ],
        ""
      );

    /*
    |--------------------------------------------------------------------------
    | NATIVE SAME-PAGE PRINT
    |--------------------------------------------------------------------------
    |
    | Same strategy as the working Sale Invoice print page.
    |
    | - no popup
    | - no iframe
    | - no cloned quotation root
    | - prints the already-rendered quotation sheet
    | - temporarily neutralizes parent layout CSS that can make Chrome
    |   print preview completely white
    |--------------------------------------------------------------------------
    */

    const printQuotation =
      useCallback(
        async () => {
          const printableNode =
            printRef.current;

          if (!printableNode) {
            setActionMessage(
              "Quotation preview is not ready to print."
            );

            return false;
          }

          const printPage =
            printableNode.closest(
              ".quotation-print-page"
            ) ||
            printableNode;

          const touchedElements =
            [];

          let current =
            printPage;

          while (
            current &&
            current !==
            document.body
          ) {
            touchedElements.push({
              element:
                current,

              cssText:
                current.getAttribute(
                  "style"
                ),
            });

            current =
              current.parentElement;
          }

          const forcePrintable =
            (
              element
            ) => {
              if (!element) {
                return;
              }

              element.style.setProperty(
                "display",
                "block",
                "important"
              );

              element.style.setProperty(
                "visibility",
                "visible",
                "important"
              );

              element.style.setProperty(
                "opacity",
                "1",
                "important"
              );

              element.style.setProperty(
                "height",
                "auto",
                "important"
              );

              element.style.setProperty(
                "min-height",
                "0",
                "important"
              );

              element.style.setProperty(
                "max-height",
                "none",
                "important"
              );

              element.style.setProperty(
                "overflow",
                "visible",
                "important"
              );

              element.style.setProperty(
                "transform",
                "none",
                "important"
              );
            };

          touchedElements.forEach(
            ({
              element,
            }) => {
              forcePrintable(
                element
              );
            }
          );

          document.body.classList.add(
            "quotation-native-print"
          );

          let cleanedUp =
            false;

          const cleanup =
            () => {
              if (cleanedUp) {
                return;
              }

              cleanedUp =
                true;

              window.removeEventListener(
                "afterprint",
                cleanup
              );

              document.body.classList.remove(
                "quotation-native-print"
              );

              touchedElements.forEach(
                ({
                  element,
                  cssText,
                }) => {
                  if (
                    cssText ===
                    null
                  ) {
                    element.removeAttribute(
                      "style"
                    );
                  } else {
                    element.setAttribute(
                      "style",
                      cssText
                    );
                  }
                }
              );
            };

          window.addEventListener(
            "afterprint",
            cleanup,
            {
              once:
                true,
            }
          );

          try {
            if (
              document.fonts?.ready
            ) {
              await document
                .fonts
                .ready;
            }

            const images =
              Array.from(
                printableNode
                  .querySelectorAll(
                    "img"
                  )
              );

            await Promise.all(
              images.map(
                (
                  image
                ) => {
                  if (
                    image.complete
                  ) {
                    return Promise.resolve();
                  }

                  return new Promise(
                    (
                      resolve
                    ) => {
                      const done =
                        () => {
                          image.removeEventListener(
                            "load",
                            done
                          );

                          image.removeEventListener(
                            "error",
                            done
                          );

                          resolve();
                        };

                      image.addEventListener(
                        "load",
                        done
                      );

                      image.addEventListener(
                        "error",
                        done
                      );
                    }
                  );
                }
              )
            );

            await new Promise(
              (
                resolve
              ) =>
                window.requestAnimationFrame(
                  () =>
                    window.requestAnimationFrame(
                      resolve
                    )
                )
            );

            window.focus();
            window.print();

            window.setTimeout(
              cleanup,
              60000
            );

            return true;
          } catch (
            error
          ) {
            cleanup();

            setActionMessage(
              "Unable to prepare the quotation for printing."
            );

            return false;
          }
        },
        []
      );

    /*
    |--------------------------------------------------------------------------
    | AUTOMATIC PRINT MODE
    |--------------------------------------------------------------------------
    */

    useEffect(() => {
      const params =
        new URLSearchParams(
          location.search
        );

      const mode =
        params.get(
          "mode"
        ) ||
        params.get(
          "print"
        );

      if (
        quotation &&
        !loading &&
        (
          mode === "print" ||
          mode === "true"
        )
      ) {
        const timeout =
          window.setTimeout(
            () => {
              printQuotation();
            },
            450
          );

        return () => {
          window.clearTimeout(
            timeout
          );
        };
      }

      return undefined;
    }, [
      quotation,
      loading,
      location.search,
      printQuotation,
    ]);

    /*
    |--------------------------------------------------------------------------
    | ACTIONS
    |--------------------------------------------------------------------------
    */

    const handlePrint =
      () => {
        setActionMessage(
          ""
        );

        const started =
          printQuotation();

        if (!started) {
          setActionMessage(
            "Unable to prepare the quotation for printing. Please refresh and try again."
          );
        }
      };

    const handleDownload =
      () => {
        setActionMessage(
          "Choose “Save as PDF” in the print dialog to download the quotation."
        );

        const started =
          printQuotation();

        if (!started) {
          setActionMessage(
            "Unable to prepare the quotation for PDF. Please refresh and try again."
          );
        }
      };

    const handleWhatsAppShare =
      () => {
        const text = [
          `${company.name} - Quotation ${documentNumber}`,

          `Customer: ${customer.name}`,

          `Grand Total: ${formatCurrency(
            totals.grandTotal
          )}`,

          `View: ${window.location.href}`,
        ].join("\n");

        const url =
          `https://wa.me/?text=${encodeURIComponent(
            text
          )}`;

        window.open(
          url,
          "_blank",
          "noopener,noreferrer"
        );
      };

    /*
    |--------------------------------------------------------------------------
    | LOADING / EMPTY
    |--------------------------------------------------------------------------
    */

    if (
      loading &&
      !quotation
    ) {
      return (
        <div className="quotation-print-state">
          <FiRefreshCw className="quotation-print-spin" />

          <strong>
            Loading quotation...
          </strong>
        </div>
      );
    }

    if (!quotation) {
      return (
        <div className="quotation-print-state">
          <strong>
            Quotation not found.
          </strong>

          <button
            type="button"
            onClick={() =>
              history.push(
                "/billing/quotations"
              )
            }
          >
            Back to Quotations
          </button>
        </div>
      );
    }

    /*
    |--------------------------------------------------------------------------
    | RENDER
    |--------------------------------------------------------------------------
    */

    return (
      <div className="quotation-print-page">
        <div className="quotation-print-toolbar">
          <button
            type="button"
            className="quotation-print-toolbar-back"
            onClick={() =>
              history.goBack()
            }
          >
            <FiArrowLeft />

            Back
          </button>

          <div className="quotation-print-toolbar-actions">
            <button
              type="button"
              onClick={
                handleWhatsAppShare
              }
            >
              <FiMessageCircle />

              WhatsApp
            </button>

            <button
              type="button"
              onClick={
                handleDownload
              }
            >
              <FiDownload />

              Download PDF
            </button>

            <button
              type="button"
              className="quotation-print-primary-action"
              onClick={
                handlePrint
              }
            >
              <FiPrinter />

              Print
            </button>
          </div>
        </div>

        {actionMessage && (
          <div className="quotation-print-action-message">
            {actionMessage}
          </div>
        )}

        <main
          ref={printRef}
          className="quotation-print-sheet"
          id="quotation-print-sheet"
        >
          <header className="quotation-print-header">
            <div className="quotation-print-company">
              {quotationBusinessSnapshot
                ?.invoiceBranding
                ?.logo ||
              quotationBusinessSnapshot
                ?.logo ||
              quotationBusinessSnapshot
                ?.branding
                ?.logo ? (
                <img
                  src={
                    quotationBusinessSnapshot
                      ?.invoiceBranding
                      ?.logo ||
                    quotationBusinessSnapshot
                      ?.logo ||
                    quotationBusinessSnapshot
                      ?.branding
                      ?.logo
                  }
                  alt={`${company.name} logo`}
                  className="quotation-print-company-logo"
                />
              ) : (
                <div className="quotation-print-company-initial">
                  {getInitial(
                    company.name
                  )}
                </div>
              )}

              <div>
                <h1>
                  {company.name}
                </h1>

                <p>
                  {company.address}
                </p>

                {company.mobile && (
                  <strong>
                    Mobile:{" "}
                    {company.mobile}
                  </strong>
                )}

                {company.email && (
                  <span>
                    Email:{" "}
                    {company.email}
                  </span>
                )}

                {company.gstin && (
                  <span>
                    GSTIN:{" "}
                    {company.gstin}
                  </span>
                )}

                {company.pan && (
                  <span>
                    PAN:{" "}
                    {company.pan}
                  </span>
                )}
              </div>
            </div>

            <div className="quotation-print-document-title">
              <h2>
                QUOTATION
              </h2>

              <span
                className={`quotation-print-status quotation-print-status-${String(
                  status
                ).toLowerCase()}`}
              >
                {formatLabel(
                  status
                )}
              </span>
            </div>
          </header>

          <div className="quotation-print-accent-line" />

          <section className="quotation-print-party-grid">
            <div className="quotation-print-party-box">
              <span className="quotation-print-box-label">
                BILL TO
              </span>

              <strong>
                {customer.name}
              </strong>

              <p>
                {customer.billingAddress}
              </p>

              {customer.mobile && (
                <span>
                  Mobile:{" "}
                  {customer.mobile}
                </span>
              )}

              {customer.email && (
                <span>
                  Email:{" "}
                  {customer.email}
                </span>
              )}

              {customer.gstin && (
                <span>
                  GSTIN:{" "}
                  {customer.gstin}
                </span>
              )}
            </div>

            <div className="quotation-print-party-box">
              <span className="quotation-print-box-label">
                SHIP TO
              </span>

              <strong>
                {customer.name}
              </strong>

              <p>
                {customer.shippingAddress}
              </p>
            </div>

            <div className="quotation-print-meta-box">
              <div>
                <span>
                  Quotation No.
                </span>

                <strong>
                  {documentNumber}
                </strong>
              </div>

              <div>
                <span>
                  Quotation Date
                </span>

                <strong>
                  {formatDate(
                    documentDate
                  )}
                </strong>
              </div>

              <div>
                <span>
                  Valid Until
                </span>

                <strong>
                  {formatDate(
                    validUntil
                  )}
                </strong>
              </div>

              <div>
                <span>
                  Sales Channel
                </span>

                <strong>
                  {formatLabel(
                    salesChannel
                  )}
                </strong>
              </div>

              <div>
                <span>
                  Price Mode
                </span>

                <strong>
                  {String(
                    priceMode
                  ).toUpperCase() ===
                  "TAX_INCLUSIVE"
                    ? "Tax Inclusive"
                    : String(
                        priceMode
                      ).toUpperCase() ===
                      "TAX_EXCLUSIVE"
                      ? "Tax Exclusive"
                      : formatLabel(
                          priceMode
                        )}
                </strong>
              </div>
            </div>
          </section>

          <section className="quotation-print-items">
            <table>
              <thead>
                <tr>
                  <th>
                    S.No
                  </th>

                  <th>
                    Item
                  </th>

                  <th>
                    HSN/SAC
                  </th>

                  <th>
                    Qty
                  </th>

                  <th>
                    Rate
                  </th>

                  <th>
                    Discount
                  </th>

                  <th>
                    Tax
                  </th>

                  <th>
                    Amount
                  </th>
                </tr>
              </thead>

              <tbody>
                {items.length ===
                0 ? (
                  <tr>
                    <td
                      colSpan={8}
                      className="quotation-print-empty-items"
                    >
                      No items available.
                    </td>
                  </tr>
                ) : (
                  items.map(
                    (
                      item,
                      index
                    ) => (
                      <tr
                        key={
                          item.id
                        }
                      >
                        <td>
                          {index + 1}
                        </td>

                        <td>
                          <strong>
                            {item.itemName}
                          </strong>

                          {item.description &&
                            item.description !==
                              item.itemName && (
                              <small>
                                {item.description}
                              </small>
                            )}

                          {item.isManual && (
                            <small className="quotation-print-manual-item">
                              Manual Item
                            </small>
                          )}
                        </td>

                        <td>
                          {item.hsnSacCode}
                        </td>

                        <td>
                          {item.quantity}
                          {item.unit &&
                          item.unit.toLowerCase() !==
                            "none"
                            ? ` ${String(
                                item.unit
                              ).toUpperCase()}`
                            : ""}
                        </td>

                        <td>
                          {formatCurrency(
                            item.rate
                          )}
                        </td>

                        <td>
                          {item.discountAmount >
                          0 ? (
                            <>
                              <strong>
                                {formatCurrency(
                                  item.discountAmount
                                )}
                              </strong>

                              {String(
                                item.discountType
                              ).toUpperCase() ===
                                "PERCENTAGE" && (
                                <small>
                                  {item.discountValue}%
                                </small>
                              )}

                              {String(
                                item.discountType
                              ).toUpperCase() ===
                                "FLAT" && (
                                <small>
                                  Flat
                                </small>
                              )}
                            </>
                          ) : (
                            "-"
                          )}
                        </td>

                        <td>
                          {item.taxAmount >
                          0 ? (
                            <>
                              <strong>
                                {formatCurrency(
                                  item.taxAmount
                                )}
                              </strong>

                              {item.taxPercentage >
                                0 && (
                                <small>
                                  {item.taxPercentage}%
                                </small>
                              )}
                            </>
                          ) : (
                            "-"
                          )}
                        </td>

                        <td>
                          <strong>
                            {formatCurrency(
                              item.totalAmount
                            )}
                          </strong>
                        </td>
                      </tr>
                    )
                  )
                )}
              </tbody>
            </table>
          </section>

          <section className="quotation-print-bottom">
            <div className="quotation-print-notes">
              <div>
                <h3>
                  Notes
                </h3>

                <p>
                  {notes ||
                    "No notes added."}
                </p>
              </div>

              <div>
                <h3>
                  Terms & Conditions
                </h3>

                <p>
                  {terms ||
                    "No terms and conditions added."}
                </p>
              </div>
            </div>

            <div className="quotation-print-summary">
              <div>
                <span>
                  Subtotal
                </span>

                <strong>
                  {formatCurrency(
                    totals.subtotal
                  )}
                </strong>
              </div>

              <div className="quotation-print-discount-row">
                <span>
                  Total Discount
                </span>

                <strong>
                  {totals.totalDiscount >
                  0
                    ? `- ${formatCurrency(
                        totals.totalDiscount
                      )}`
                    : formatCurrency(
                        0
                      )}
                </strong>
              </div>

              <div>
                <span>
                  Taxable Amount
                </span>

                <strong>
                  {formatCurrency(
                    totals.taxableAmount
                  )}
                </strong>
              </div>

              {totals.isInterState ? (
                <div>
                  <span>
                    IGST
                  </span>

                  <strong>
                    {formatCurrency(
                      totals.igstAmount
                    )}
                  </strong>
                </div>
              ) : (
                <>
                  <div>
                    <span>
                      CGST
                    </span>

                    <strong>
                      {formatCurrency(
                        totals.cgstAmount
                      )}
                    </strong>
                  </div>

                  <div>
                    <span>
                      SGST
                    </span>

                    <strong>
                      {formatCurrency(
                        totals.sgstAmount
                      )}
                    </strong>
                  </div>
                </>
              )}

              <div>
                <span>
                  Total Tax
                </span>

                <strong>
                  {formatCurrency(
                    totals.totalTax
                  )}
                </strong>
              </div>

              {totals.roundOffAmount !==
                0 && (
                <div>
                  <span>
                    Round Off
                  </span>

                  <strong>
                    {formatCurrency(
                      totals.roundOffAmount
                    )}
                  </strong>
                </div>
              )}

              <div className="quotation-print-grand-total">
                <span>
                  Grand Total
                </span>

                <strong>
                  {formatCurrency(
                    totals.grandTotal
                  )}
                </strong>
              </div>
            </div>
          </section>

          <footer className="quotation-print-footer">
            <span>
              This is a computer-generated quotation.
            </span>

            <InvoiceSignature
              businessSnapshot={
                quotationBusinessSnapshot
              }
            />
          </footer>
        </main>
      </div>
    );
  };

export default QuotationPrintPage;
